<!-- @format -->

<template>
	<div class="contract-residual-values-table-modal">
		<div class="bg-primary-light mb-6 px-10 py-3 rounded">
			<CoreTwoColumnsRow>
				<template #left
					><span>{{ $t('§caf.list_price_including_extras') }}</span></template
				>
				<template #right
					><span class="text-bold">{{
						displayAsCurrency(residualValueBaseAmount, RV_CURRENCY_DIGITS, currencyCode)
					}}</span></template
				>
			</CoreTwoColumnsRow>
			<CoreTwoColumnsRow>
				<template #left
					><span>{{ $t('§contracts.depreciation_period') }}</span></template
				>
				<template #right>
					<CoreDropdown
						v-model="depreciationPeriodYears"
						:options="depreciationPeriodOptions"
						:sort="false"
						:width="140"
						:disabled="isReadonly || isBusy || saving || pendingCopy"
						@update:modelValue="onDepreciationPeriodChange"
					/>
				</template>
			</CoreTwoColumnsRow>
		</div>

		<div
			v-if="pendingCopy"
			class="bg-warning-light mb-6 px-10 py-3 rounded flex flex-col justify-start gap-2"
		>
			<span>{{ $t('§contracts.copy_equipment_type_rvt_overwrites_current') }}</span>
			<div class="flex gap-2">
				<CoreButton
					label="§ui_button.proceed"
					variant="primary"
					icon="pi-copy"
					@click="applyCopy"
				/>
				<CoreButton
					label="§ui_button.keep_current_values"
					variant="tertiary"
					icon="pi-times"
					@click="cancelCopy"
				/>
			</div>
		</div>
		<div
			v-else-if="pendingPeriod !== null"
			class="bg-warning-light mb-6 px-10 py-3 rounded flex flex-col justify-start gap-2"
		>
			<span>{{ $t('§contracts.recalculate_rv_table_discards_manual_changes') }}</span>
			<div class="flex gap-2">
				<CoreButton
					label="§ui_button.proceed"
					variant="primary"
					icon="pi-refresh"
					@click="confirmPeriodChange"
				/>
				<CoreButton
					label="§ui_button.keep_current_values"
					variant="tertiary"
					icon="pi-times"
					@click="cancelPeriodChange"
				/>
			</div>
		</div>

		<DataTable
			:value="table"
			:editMode="isReadonly || isBusy ? undefined : 'cell'"
			:loading="isBusy"
			@cell-edit-init="onCellEditInit"
			@cell-edit-complete="onCellEditComplete"
			@cell-edit-cancel="onCellEditCancel"
			class="residual-values-table rv-matrix rv-matrix--single"
			:class="{ 'filter-blur': isBusy }"
			responsiveLayout="scroll"
		>
			<Column
				field="months"
				headerClass="rv-mh"
				bodyClass="rv-mcol"
			>
				<template #header>
					<span class="font-bold">
						{{ $t('§eq_type.months') }}
					</span>
				</template>
				<template #body="slotProps">
					<span>
						{{
							slotProps.data.months === 1
								? $t('§eq_type.day_1')
								: slotProps.data.months
						}}
					</span>
				</template>
			</Column>

			<template
				v-for="key in displayedUtilizationKeys"
				:key="key"
			>
				<Column
					:field="key"
					:pt="{
						headerCell: { class: 'rv-gh', 'data-g': 0 },
						bodyCell: { class: 'rv-gcol', 'data-g': 0 },
					}"
				>
					<template #header>
						<span class="rv-gh__label">
							<span class="pctlab font-bold">{{ key }}</span>
							<span class="util">{{ $t('§contracts.utilisation') }}</span>
						</span>
					</template>
					<template #body="slotProps">
						<CoreCurrencyPercentageInput
							:modelValue="slotProps.data[key]"
							:baseValue="Number(residualValueBaseAmount) || 0"
							:currency="currencyCode"
							:currencyDigits="RV_CURRENCY_DIGITS"
							:percentageDigits="1"
							displayOnly
						/>
					</template>
					<template #editor="slotProps">
						<CoreCurrencyPercentageInput
							v-model="slotProps.data[key]"
							class="modal-residual-values-table__editor"
							:baseValue="Number(residualValueBaseAmount) || 0"
							:currency="currencyCode"
							:currencyDigits="RV_CURRENCY_DIGITS"
							:percentageDigits="1"
							:showBorder="false"
							autofocus
							:autofocusSide="editFocusSide"
							:validator="getCellValidator(slotProps.data.months, key)"
							:customNotificationMessage="
								getCellValidationMessage(slotProps.data.months, key)
							"
						/>
					</template>
				</Column>
			</template>
		</DataTable>

		<div class="flex justify-start gap-4 mt-8">
			<CoreButton
				v-if="!isReadonly"
				label="§ui_button.save"
				variant="primary"
				icon="pi-save"
				:disabled="
					saving ||
					isBusy ||
					hasCellValidationErrors ||
					pendingCopy ||
					pendingPeriod !== null
				"
				:loading="saving"
				@click="handleSave"
			/>
			<CoreButton
				v-if="canCopyUnitTypeTable"
				label="§ui_button.copy_equipment_type_table"
				variant="secondary"
				icon="pi-copy"
				:disabled="
					saving ||
					isBusy ||
					pendingCopy ||
					pendingPeriod !== null ||
					tableMatchesUnitTypeCopy
				"
				@click="onCopyUnitTypeTable"
			/>
			<CoreButton
				label="§ui_button.cancel"
				variant="tertiary"
				icon="pi-times"
				:disabled="saving"
				@click="closeModal"
			/>
		</div>
	</div>
</template>

<script>
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import CoreCurrencyPercentageInput from '@/components/core/CoreCurrencyPercentageInput.vue'
import CoreButton from '@/components/core/CoreButton.vue'
import { useModalStore } from '@/stores/modal'
import { useContractStore } from '@/stores/contract'
import ApiService from '@/api/ApiService'
import CoreTwoColumnsRow from '@/components/core/CoreTwoColumnsRow.vue'
import CoreDropdown from '@/components/core/CoreDropdown.vue'
import {
	DEFAULT_RV_DEPRECIATION_PERIOD_YEARS,
	RV_CURRENCY_DIGITS,
	RV_RESIDUAL_FLOOR,
} from '@/data/constants.js'
import { rvDepreciationPeriodOptions } from '@/helpers/rvDepreciationPeriodOptions.js'
import { roundHalfUp } from '@/helpers/roundHalfUp.js'

const RECALCULATION_HEAD_MONTHS = [0, 1, 12]
const COPY_MATCH_TOLERANCE_EUROS = 1

export default {
	name: 'ContractResidualValuesTableModal',

	components: {
		DataTable,
		Column,
		CoreCurrencyPercentageInput,
		CoreButton,
		CoreTwoColumnsRow,
		CoreDropdown,
	},

	data() {
		return {
			table: [],
			loading: false,
			saving: false,
			residualValueBaseAmount: 0,
			currencyCodeFromApi: null,
			depreciationPeriodYears: DEFAULT_RV_DEPRECIATION_PERIOD_YEARS,
			lastAppliedPeriod: DEFAULT_RV_DEPRECIATION_PERIOD_YEARS,
			recalculating: false,
			editFocusSide: null,
			cellValidationErrors: {},
			unitTypeCopy: null,
			baselineTable: null,
			pendingCopy: false,
			pendingPeriod: null,
			RV_CURRENCY_DIGITS: RV_CURRENCY_DIGITS,
		}
	},

	created() {
		this.modalStore = useModalStore()
		this.contractStore = useContractStore()
	},

	computed: {
		isReadonly() {
			const data = this.contractStore.contractData
			return !!data?.approved || !this.hasPermission('manage contracts')
		},

		isBusy() {
			return this.loading || this.recalculating
		},

		displayedUtilizationKeys() {
			return ['100%']
		},

		contractId() {
			return this.modalStore.data?.contractId
		},

		lesseeBuyBackObligation() {
			return this.modalStore.data?.lesseeBuyBackObligation
		},

		currencyCode() {
			return (
				this.currencyCodeFromApi || this.contractStore.contractData?.currency_code || 'EUR'
			)
		},

		depreciationPeriodOptions() {
			return rvDepreciationPeriodOptions(this.$t)
		},

		hasCellValidationErrors() {
			return Object.keys(this.cellValidationErrors).length > 0
		},

		canCopyUnitTypeTable() {
			return !this.isReadonly && this.unitTypeCopy?.rows.length > 0
		},

		editedSinceBaseline() {
			return this.baselineTable !== null && this.serializeTable() !== this.baselineTable
		},

		tableMatchesUnitTypeCopy() {
			if (
				!this.unitTypeCopy ||
				Number(this.lastAppliedPeriod) !== Number(this.unitTypeCopy.period)
			) {
				return false
			}

			const copy = this.unitTypeCopy.rows
			return (
				this.table.length === copy.length &&
				this.table.every(
					(row, i) =>
						row.months === copy[i].months &&
						Math.abs(Number(row['100%']) - Number(copy[i]['100%'])) <=
							COPY_MATCH_TOLERANCE_EUROS,
				)
			)
		},
	},

	mounted() {
		this.fetchData()
	},

	methods: {
		closeModal() {
			this.modalStore.setClose()
		},

		getTableData(rvt) {
			const utilizationKeys = Object.keys(rvt)
			const months = [
				...new Set(
					utilizationKeys.flatMap((utilization) =>
						Object.keys(rvt[utilization]).map(Number),
					),
				),
			].sort((a, b) => a - b)

			return months.map((month) => {
				const row = { months: month }
				utilizationKeys.forEach((utilization) => {
					row[`${Math.round(Number(utilization.replace('-', '.')) * 100)}%`] = roundHalfUp(
						Number(rvt[utilization][month] ?? 0),
						RV_CURRENCY_DIGITS,
					)
				})
				return row
			})
		},

		filterToFullUtilization(rvt) {
			const fullUtilizationKey = Object.keys(rvt || {}).find(
				(key) => Number(key.replace('-', '.')) === 1,
			)
			return fullUtilizationKey ? { [fullUtilizationKey]: rvt[fullUtilizationKey] } : rvt
		},

		async fetchData() {
			this.loading = true
			try {
				const response = await ApiService.get('contract.residual_values_table.show', {
					contract: this.contractId,
				})
				const data = response.data
				const copy = data.unit_type_copy
				this.unitTypeCopy = copy
					? {
							period:
								Number(copy.depreciation_period_years) ||
								DEFAULT_RV_DEPRECIATION_PERIOD_YEARS,
							rows: this.getTableData(
								this.filterToFullUtilization(copy.residual_values_table),
							),
						}
					: null
				this.pendingPeriod = null
				this.residualValueBaseAmount = data.residual_value_base_amount ?? 0
				this.currencyCodeFromApi = data.currency_code || null
				this.depreciationPeriodYears =
					Number(data.depreciation_period_years) || DEFAULT_RV_DEPRECIATION_PERIOD_YEARS
				this.lastAppliedPeriod = this.depreciationPeriodYears
				this.table = this.getTableData(
					this.filterToFullUtilization(data.residual_values_table),
				)
				this.baselineTable = this.serializeTable()
			} catch (error) {
				console.error('Error fetching residual values table:', error)
				this.closeModal()
			} finally {
				this.loading = false
			}
		},

		transformTableForBackend() {
			const rvt = {}
			const keysToSave = ['100%']

			this.table.forEach((row) => {
				keysToSave.forEach((key) => {
					const numberKey = Number(key.slice(0, -1)) / 100
					const laravelFriendlyKey = numberKey.toString().replace('.', '-')

					if (rvt[laravelFriendlyKey] === undefined) {
						rvt[laravelFriendlyKey] = {}
					}
					rvt[laravelFriendlyKey][row.months] = row[key]
				})
			})

			return rvt
		},

		async handleSave() {
			this.saving = true
			try {
				const payload = {
					residual_values_table: this.transformTableForBackend(),
					depreciation_period_years: this.depreciationPeriodYears,
				}
				if (this.lesseeBuyBackObligation !== undefined) {
					payload.lessee_buy_back_obligation = this.lesseeBuyBackObligation
				}

				await ApiService.post('contract.residual_values_table.store', payload, {
					contract: this.contractId,
				})

				this.contractStore.notification('success', '§ui_message.update_successful')

				await this.contractStore.getContractData({ contract: this.contractId })

				this.closeModal()
			} catch (error) {
				console.error('Error saving residual values table:', error)
				this.contractStore.notification('error', '§ui_message.update_failed')
			} finally {
				this.saving = false
			}
		},

		valueAtMonth(months) {
			const row = this.table.find((entry) => entry.months === months)
			return Number(row?.['100%'] ?? 0)
		},

		headPayload() {
			return Object.fromEntries(
				RECALCULATION_HEAD_MONTHS.map((months) => [months, this.valueAtMonth(months)]),
			)
		},

		serializeTable() {
			return JSON.stringify(this.table.map((row) => [row.months, Number(row['100%'])]))
		},

		onCopyUnitTypeTable() {
			if (this.editedSinceBaseline) {
				this.pendingCopy = true
				return
			}

			this.applyCopy()
		},

		applyCopy() {
			const rows = (this.unitTypeCopy?.rows || []).filter((row) =>
				Number.isFinite(row['100%']),
			)

			if (rows.length === 0) {
				this.pendingCopy = false
				return
			}

			this.table = rows.map((row) => ({ ...row }))
			this.depreciationPeriodYears = this.unitTypeCopy.period
			this.lastAppliedPeriod = this.unitTypeCopy.period
			this.cellValidationErrors = {}
			this.baselineTable = this.serializeTable()
			this.pendingCopy = false
		},

		cancelCopy() {
			this.pendingCopy = false
		},

		async onDepreciationPeriodChange(newValue) {
			const period = Number(newValue)
			if (period === this.lastAppliedPeriod) {
				this.pendingPeriod = null
				return
			}

			if (this.editedSinceBaseline) {
				this.pendingPeriod = period
				return
			}

			await this.recalculate(period)
		},

		confirmPeriodChange() {
			const period = this.pendingPeriod
			this.pendingPeriod = null
			return this.recalculate(period)
		},

		cancelPeriodChange() {
			this.pendingPeriod = null
			this.depreciationPeriodYears = this.lastAppliedPeriod
		},

		async recalculate(period) {
			this.recalculating = true
			try {
				const response = await ApiService.post(
					'contract.residual_values_table.calculate',
					{
						depreciation_period_years: period,
						head: this.headPayload(),
					},
					{ contract: this.contractId },
				)

				this.table = this.getTableData(
					this.filterToFullUtilization(response.data.residual_values_table),
				)
				this.cellValidationErrors = {}
				this.lastAppliedPeriod = period
				this.baselineTable = this.serializeTable()
			} catch (error) {
				console.error('Error recalculating residual values table:', error)
				this.depreciationPeriodYears = this.lastAppliedPeriod
				this.contractStore.notification('error', '§ui_message.update_failed')
			} finally {
				this.recalculating = false
			}
		},

		onCellEditInit(event) {
			const target = event.originalEvent?.target
			this.editFocusSide = target?.closest?.('.core-currency-percentage-input__percentage')
				? 'percentage'
				: 'currency'
		},

		getCellValidationMessage(months, field) {
			const key = `${months}-${field}`
			return this.cellValidationErrors[key] || null
		},

		getCellValidator(months, field) {
			const key = `${months}-${field}`
			const hasError = this.cellValidationErrors[key] !== undefined

			return {
				$dirty: hasError,
				$error: hasError,
				$invalid: hasError,
				$touch: () => {},
				$reset: () => {},
			}
		},

		clearCellValidationError(key) {
			if (this.cellValidationErrors[key]) {
				delete this.cellValidationErrors[key]
				this.cellValidationErrors = { ...this.cellValidationErrors }
			}
		},

		onCellEditComplete(event) {
			const { data, newValue, field } = event
			const rowIndex = this.table.indexOf(data)
			const key = `${data.months}-${field}`

			const value = Number(newValue)

			let errorMessage = null

			if (Number.isNaN(value) || value < RV_RESIDUAL_FLOOR) {
				errorMessage = {
					severity: 'error',
					message: '§ui.residual_value_must_be_at_least_one',
				}
			} else if (rowIndex === 0 && value > Number(this.residualValueBaseAmount)) {
				errorMessage = {
					severity: 'error',
					message: '§ui.residual_value_exceeds_contract_base',
				}
			} else if (rowIndex > 0 && value > Number(this.table[rowIndex - 1][field])) {
				errorMessage = {
					severity: 'error',
					message: '§ui.residual_value_exceeds_previous',
				}
			} else if (
				RECALCULATION_HEAD_MONTHS.slice(0, -1).includes(data.months) &&
				rowIndex < this.table.length - 1 &&
				value < Number(this.table[rowIndex + 1][field])
			) {
				errorMessage = {
					severity: 'error',
					message: '§ui.residual_value_below_next',
				}
			}

			if (errorMessage) {
				this.cellValidationErrors = {
					...this.cellValidationErrors,
					[key]: errorMessage,
				}
				event.preventDefault()
				return
			}

			this.clearCellValidationError(key)
			data[field] = newValue
		},

		onCellEditCancel(event) {
			const { data, field } = event
			this.clearCellValidationError(`${data.months}-${field}`)
		},
	},
}
</script>

<style>
.contract-residual-values-table-modal {
	padding: 40px;
}
</style>
